"use client";

import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin,
  Plus,
  Pencil,
  Trash2,
  Truck,
  Wallet,
  ShieldCheck,
  Lock,
  Check,
  X,
} from "lucide-react";
import AddressForm from "./AddressForm";
import EditAddressForm from "./EditAddressForm";
import axios from "axios";
import Swal from "sweetalert2";
import PayUForm from "../paymentpage/PayUForm";
import { useMenu } from "../../context/MenuContext";
import GuestAddressForm from "./GuestAddressForm";

/* Brand accent — same value used on the product page. */
const ACCENT = "rgb(255,0,25)";

const API_BASE = "https://code.gurusport.in/api";

const money = (n) => Number(n || 0).toFixed(2);

const PaymentPage = () => {
  const [addresses, setAddresses] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [cart, setCart] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [payUData, setPayUData] = useState(null);
  const [isCartLoading, setIsCartLoading] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [showCODModal, setShowCODModal] = useState(false);
  const userId = localStorage.getItem("authUserId");
  const authToken =
    localStorage.getItem("authToken") || localStorage.getItem("guest_token");

  const {
    promoCode,
    promoDiscount,
    finalAmount,
    fetchMenuCounts,
    setCartCount,
    shippingCharge,
    fetchShippingCharge,
  } = useMenu();
  const drawerRef = useRef(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    line1: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    contact: "",
    landmark: "",
    addressType: "",
  });

  const fetchAddresses = async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      const userId = localStorage.getItem("authUserId");

      if (userId && authToken) {
        const res = await axios.get(
          `${API_BASE}/user-addresses`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        const data = res.data?.data;

        if (Array.isArray(data)) {
          setAddresses(data);
          if (data.length > 0) setSelectedAddress(data[0].id);
        } else if (typeof data === "object" && data !== null) {
          setAddresses([data]);
          setSelectedAddress(data.id);
        } else {
          setAddresses([]);
        }
      } else {
        const guestAddressString = localStorage.getItem("guestAddress");
        if (guestAddressString) {
          const guestAddress = JSON.parse(guestAddressString);
          setAddresses([guestAddress]);
          setSelectedAddress(null);
        } else {
          setAddresses([]);
        }
      }
    } catch (err) {
      console.error("❌ Error fetching addresses:", err);
      setAddresses([]);
    }
  };

  const fetchCart = async () => {
    setIsCartLoading(true);
    try {
      const authToken =
        localStorage.getItem("authToken") ||
        localStorage.getItem("guest_token");

      let res;
      if (authToken && userId) {
        res = await axios.get(`${API_BASE}/cart`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
      } else {
        res = await axios.get(
          `${API_BASE}/cart?guest_token=${authToken}`
        );
      }

      const data = res.data?.data || [];

      if (Array.isArray(data) && data.length > 0) {
        const allItems = data.flatMap((cart) => cart.items || []);
        if (allItems.length > 0) {
          setCart(allItems);
          setCartCount(allItems.length);
          const totalPrice = allItems.reduce(
            (acc, item) => acc + Number.parseFloat(item.total || 0),
            0
          );
          setTotal(totalPrice);
        } else {
          setCart([]);
          setCartCount(0);
          setTotal(0);
        }
      } else {
        setCart([]);
        setCartCount(0);
        setTotal(0);
      }
    } catch (err) {
      console.error("❌ Error fetching cart:", err);
      setCart([]);
      setCartCount(0);
      setTotal(0);
    } finally {
      setIsCartLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
    fetchCart();
    fetchShippingCharge();
  }, []);

  // Close Drawer on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showDrawer &&
        drawerRef.current &&
        !drawerRef.current.contains(event.target)
      ) {
        setShowDrawer(false);
        setEditingIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDrawer]);

  // Lock background scroll while the confirm modal is open
  useEffect(() => {
    document.body.style.overflow = showCODModal ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showCODModal]);

  const handleSubmitAddress = async () => {
    await fetchAddresses();
    setFormData({
      name: "",
      email: "",
      line1: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
      contact: "",
      landmark: "",
      addressType: "",
    });
    setShowDrawer(false);
    setEditingIndex(null);
  };

  const handleEdit = (index) => {
    const authToken = localStorage.getItem("authToken");
    const userId = localStorage.getItem("authUserId");

    if (authToken && userId) {
      const addr = addresses[index];
      setFormData({
        id: addr.id,
        name: addr.name || "",
        email: addr.email || "",
        line1: addr.street || "",
        city: addr.city?.id || "",
        state: addr.state?.id || "",
        country: addr.country?.id || "",
        pincode: addr.zipcode || "",
        phone_number: addr.phone_number || "",
        landmark: addr.apartment_suite || "",
        addressType: addr.address_type || addr.addressType || "",
      });
    } else {
      const guestAddressString = localStorage.getItem("guestAddress");
      if (guestAddressString) {
        const addr = JSON.parse(guestAddressString);
        setFormData({
          name: addr.guest_name || "",
          email: addr.guest_email || "",
          line1: addr.guest_street || "",
          city: addr.guest_city_id || "",
          state: addr.guest_state_id || "",
          country: addr.guest_country_id || "",
          pincode: addr.guest_zipcode || "",
          phone_number: addr.guest_phone_number || "",
          landmark: addr.guest_apartment_suite || "",
          addressType: addr.guest_address_type || "",
        });
      }
    }

    setEditingIndex(index);
    setShowDrawer(true);
  };

  const handleDelete = async (index) => {
    const address = addresses[index];

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this address!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const authToken = localStorage.getItem("authToken");
          const userId = localStorage.getItem("authUserId");

          if (authToken && userId) {
            await axios.delete(
              `${API_BASE}/user-addresses/${address.id}`,
              {
                headers: {
                  Authorization: `Bearer ${authToken}`,
                },
              }
            );
          } else {
            localStorage.removeItem("guestAddress");
          }

          await fetchAddresses();
          Swal.fire("Deleted!", "Your address has been deleted.", "success");
        } catch (err) {
          console.error("Error deleting address:", err);
          Swal.fire("Error!", "Failed to delete address.", "error");
        }
      }
    });
  };

  const placeCODOrder = async () => {
    const guestAddressString = localStorage.getItem("guestAddress");
    if (!selectedAddress && !guestAddressString) {
      Swal.fire(
        "Error!",
        "Please select an address before proceeding.",
        "error"
      );
      return;
    }

    try {
      setPlacingOrder(true);
      const userId = localStorage.getItem("authUserId");
      const authToken = localStorage.getItem("authToken");
      const guestToken = localStorage.getItem("guest_token");

      let payload = {
        payment_type: "cod",
      };

      if (userId && authToken) {
        payload = {
          ...payload,
          user_id: userId,
          user_address_id: selectedAddress,
        };
      } else {
        const guestAddressString = localStorage.getItem("guestAddress");
        if (!guestAddressString) {
          Swal.fire("Error!", "Guest address not found.", "error");
          return;
        }

        const guestAddress = JSON.parse(guestAddressString);
        const guestToken = localStorage.getItem("guest_token");

        const fullAddress = `${guestAddress.guest_street}, ${guestAddress.guest_apartment_suite}, ${guestAddress.guest_city_name}, ${guestAddress.guest_state_name}, ${guestAddress.guest_country_name}, ${guestAddress.guest_zipcode}`;
        const guestpincode = `${guestAddress.guest_zipcode}`;
        const guestcountry = `${guestAddress.guest_country_id}`;
        const gueststate = `${guestAddress.guest_state_id}`;
        const guestcity = `${guestAddress.guest_city_id}`;

        payload = {
          ...payload,
          guest_token: guestToken,
          name: guestAddress.guest_name,
          email: guestAddress.guest_email,
          phone: guestAddress.guest_phone_number,
          shipping_address: fullAddress,
          guest_pincode: guestpincode,
          guest_city: guestcity,
          guest_country: guestcountry,
          guest_state: gueststate,
        };
      }

      const res = await axios.post(
        `${API_BASE}/orders?guest_token=${guestToken}`,
        payload,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      const { order_code } = res.data;
      if (res.data.success === true) {
        setCart([]);
        setTotal(0);
        setCartCount(0);

        if (userId && authToken) {
          await fetchMenuCounts(userId, authToken);
          await fetchCart();
        } else {
          await setCartCount(res.data.cart || 0);
          localStorage.clear();
        }

        Swal.fire({
          title: "Success!",
          html: `Order placed successfully with COD! <br />
               <strong>Order Code:</strong> <b>${order_code}</b>`,
          icon: "success",
        });

        navigate("/orderplaced");
      }
    } catch (err) {
      const message =
        err?.response?.data?.message || "Failed to place COD order.";
      console.error("❌ Error placing order:", message);
      Swal.fire("Error!", message, "error");
    } finally {
      setPlacingOrder(false);
    }
  };

  const handleCODClick = () => {
    const guestAddressString = localStorage.getItem("guestAddress");
    if (!selectedAddress && !guestAddressString) {
      Swal.fire(
        "Error!",
        "Please select an address before proceeding.",
        "error"
      );
      return;
    }
    setShowCODModal(true);
  };

  const handleCODConfirm = () => {
    setShowCODModal(false);
    placeCODOrder();
  };

  const handleCODCancel = () => {
    setShowCODModal(false);
  };

  const handlePayU = async () => {
    const guestAddressString = localStorage.getItem("guestAddress");
    if (!selectedAddress && !guestAddressString) {
      Swal.fire(
        "Error!",
        "Please select an address before proceeding.",
        "error"
      );
      return;
    }

    try {
      setPlacingOrder(true);
      const userId = localStorage.getItem("authUserId");
      const authToken = localStorage.getItem("authToken");
      const guestToken = localStorage.getItem("guest_token");

      let payload = {
        payment_type: "payu",
      };

      if (userId && authToken) {
        payload = {
          ...payload,
          user_id: userId,
          session_id: userId,
          user_address_id: selectedAddress,
        };
      } else if (guestToken && guestAddressString) {
        const guestAddress = JSON.parse(guestAddressString);
        const fullAddress = `${guestAddress.guest_street}, ${guestAddress.guest_apartment_suite}, ${guestAddress.guest_city_name}, ${guestAddress.guest_state_name}, ${guestAddress.guest_country_name}, ${guestAddress.guest_zipcode}`;
        const guestpincode = `${guestAddress.guest_zipcode}`;
        const guestcountry = `${guestAddress.guest_country_id}`;
        const gueststate = `${guestAddress.guest_state_id}`;
        const guestcity = `${guestAddress.guest_city_id}`;

        payload = {
          ...payload,
          guest_token: guestToken,
          name: guestAddress.guest_name,
          email: guestAddress.guest_email,
          phone: guestAddress.guest_phone_number,
          shipping_address: fullAddress,
          guest_pincode: guestpincode,
          guest_city: guestcity,
          guest_country: guestcountry,
          guest_state: gueststate,
        };
      }

      const res = await axios.post(
        `${API_BASE}/orders${
          guestToken ? `?guest_token=${guestToken}` : ""
        }`,
        payload,
        {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        }
      );

      if (res.data?.payment) {
        setCart([]);
        setTotal(0);
        setCartCount(0);

        if (userId && authToken) {
          await fetchMenuCounts(userId, authToken);
          await fetchCart();
        } else {
          setCartCount(res.data.cart || 0);
          localStorage.clear();
        }

        // setPayUData(res.data.payment);
      } else {
        Swal.fire("Error!", "Failed to initiate PayU payment.", "error");
      }
    } catch (err) {
      console.error("❌ Error PayU order:", err);
      Swal.fire("Error!", "Failed to place PayU order.", "error");
    } finally {
      setPlacingOrder(false);
    }
  };

  if (payUData) {
    return <PayUForm payment={payUData} />;
  }

  /* ---------- derived ---------- */

  const shipping = Number(shippingCharge || 0);
  const discount = Number(promoDiscount || 0);

  /* Shipping is charged on COD orders only. */
  const prepaidTotal = Number(total || 0) - discount;
  const codTotal = Number(total || 0) + shipping - discount;

  const isGuest = !userId;
  const hasAddress = addresses.length > 0;
  const canPay = cart.length > 0 && !isCartLoading && !placingOrder;

  /* ---------- render ---------- */

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        {/* Header + steps */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl">
            Checkout
          </h1>
          <ol className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs font-medium">
            {[
              { label: "Cart", done: true },
              { label: "Address", done: hasAddress },
              { label: "Payment", done: false },
            ].map((step, i, arr) => (
              <li key={step.label} className="flex items-center gap-3">
                <span className="flex items-center gap-2">
                  <span
                    className={`grid h-6 w-6 place-items-center rounded-full text-[11px] font-bold ${
                      step.done
                        ? "text-white"
                        : "border border-gray-300 bg-white text-gray-500"
                    }`}
                    style={step.done ? { background: ACCENT } : undefined}
                  >
                    {step.done ? <Check size={13} /> : i + 1}
                  </span>
                  <span
                    className={
                      step.done ? "text-gray-900" : "text-gray-500"
                    }
                  >
                    {step.label}
                  </span>
                </span>
                {i < arr.length - 1 && (
                  <span className="h-px w-6 bg-gray-300 sm:w-10" />
                )}
              </li>
            ))}
          </ol>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-start">
          {/* ---------------- LEFT: address ---------------- */}
          <div className="lg:col-span-7">
            <section className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight">
                    Delivery address
                  </h2>
                  <p className="mt-0.5 text-sm text-gray-500">
                    {isGuest
                      ? "Checking out as a guest"
                      : "Where should we send this order?"}
                  </p>
                </div>

                {hasAddress && !isGuest && !showDrawer && (
                  <button
                    onClick={() => setShowDrawer(true)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold transition hover:border-gray-900"
                  >
                    <Plus size={15} />
                    Add new
                  </button>
                )}
              </div>

              {/* Inline address form */}
              {showDrawer && (
                <div
                  ref={drawerRef}
                  className="mt-5 rounded-xl border border-gray-200 bg-gray-50 p-4"
                >
                  {editingIndex !== null ? (
                    <EditAddressForm
                      formData={formData}
                      setFormData={setFormData}
                      onClose={() => {
                        setShowDrawer(false);
                        setEditingIndex(null);
                      }}
                      onSubmit={handleSubmitAddress}
                    />
                  ) : (
                    <AddressForm
                      formData={formData}
                      addresses={addresses}
                      setFormData={setFormData}
                      onClose={() => {
                        setShowDrawer(false);
                        setEditingIndex(null);
                      }}
                      onSubmit={handleSubmitAddress}
                    />
                  )}
                </div>
              )}

              <div className="mt-5">
                {/* Empty state */}
                {!showDrawer && addresses.length === 0 && (
                  <div className="rounded-xl border border-dashed border-gray-300 px-6 py-10 text-center">
                    <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-gray-100">
                      <MapPin size={22} className="text-gray-700" />
                    </span>
                    <p className="mt-3 font-semibold">
                      {isGuest
                        ? "Add a delivery address"
                        : "No address saved yet"}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      We need this to deliver your order.
                    </p>
                    <button
                      onClick={() => setShowDrawer(true)}
                      className="mt-4 inline-flex h-11 items-center gap-2 rounded-full px-6 text-sm font-bold uppercase tracking-wider text-white transition hover:brightness-90"
                      style={{ background: ACCENT }}
                    >
                      <Plus size={16} />
                      Add address
                    </button>
                  </div>
                )}

                {/* Logged-in addresses */}
                {!isGuest &&
                  addresses.map((addr, index) => {
                    const active = selectedAddress === addr.id;
                    return (
                      <label
                        key={addr.id || index}
                        className={`mb-3 flex cursor-pointer gap-4 rounded-xl border p-4 transition last:mb-0 ${
                          active
                            ? "border-gray-900 bg-gray-50"
                            : "border-gray-200 hover:border-gray-400"
                        }`}
                      >
                        <input
                          type="radio"
                          name="selectedAddress"
                          checked={active}
                          onChange={() => setSelectedAddress(addr.id)}
                          className="mt-1 h-4 w-4 shrink-0 accent-gray-900"
                        />

                        <div className="min-w-0 flex-1 text-sm">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-gray-900">
                              {addr.name}
                            </span>
                            {(addr.address_type || addr.addressType) && (
                              <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-gray-600">
                                {addr.address_type || addr.addressType}
                              </span>
                            )}
                          </div>

                          <p className="mt-1.5 leading-relaxed text-gray-600">
                            {addr.street}, {addr.apartment_suite},{" "}
                            {addr.city?.name || addr.city},{" "}
                            {addr.state?.name || addr.state},{" "}
                            {addr.country?.name || addr.country} —{" "}
                            {addr.zipcode}
                          </p>

                          <p className="mt-1 text-gray-500">
                            {addr.phone_number || addr.contact}
                            {addr.email ? ` · ${addr.email}` : ""}
                          </p>
                        </div>

                        <div className="flex shrink-0 flex-col items-end gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              handleEdit(index);
                            }}
                            aria-label="Edit address"
                            className="rounded-full p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              handleDelete(index);
                            }}
                            aria-label="Delete address"
                            className="rounded-full p-2 text-gray-500 transition hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </label>
                    );
                  })}

                {/* Guest address */}
                {isGuest &&
                  addresses.map((addr, index) => (
                    <div
                      key={addr.id || index}
                      className="rounded-xl border border-gray-900 bg-gray-50 p-4"
                    >
                      <div className="flex gap-4">
                        <div className="min-w-0 flex-1 text-sm">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold capitalize text-gray-900">
                              {addr.guest_name}
                            </span>
                            {(addr.guest_address_type || addr.addressType) && (
                              <span className="rounded-full bg-white px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-gray-600">
                                {addr.guest_address_type || addr.addressType}
                              </span>
                            )}
                          </div>

                          <p className="mt-1.5 leading-relaxed text-gray-600">
                            {addr.guest_street}, {addr.guest_apartment_suite},{" "}
                            {addr.guest_city_name || addr.city},{" "}
                            {addr.guest_state_name || addr.state},{" "}
                            {addr.guest_country_name || addr.country} —{" "}
                            {addr.guest_zipcode}
                          </p>

                          <p className="mt-1 text-gray-500">
                            {addr.guest_phone_number || addr.contact}
                            {addr.guest_email ? ` · ${addr.guest_email}` : ""}
                          </p>
                        </div>

                        <div className="flex shrink-0 flex-col items-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleEdit(index)}
                            aria-label="Edit address"
                            className="rounded-full p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(index)}
                            aria-label="Delete address"
                            className="rounded-full p-2 text-gray-500 transition hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </section>

            {/* Reassurance strip */}
            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 px-1 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1.5">
                <Lock size={14} /> Encrypted checkout
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck size={14} /> Card details never stored
              </span>
            </div>
          </div>

          {/* ---------------- RIGHT: summary ---------------- */}
          <div className="lg:col-span-5 lg:sticky lg:top-6">
            <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
              <div className="border-b border-gray-200 px-5 py-4 sm:px-6">
                <h2 className="text-lg font-semibold tracking-tight">
                  Your order
                </h2>
                <p className="mt-0.5 text-sm text-gray-500">
                  {cart.length} {cart.length === 1 ? "item" : "items"}
                </p>
              </div>

              {/* Items */}
              <div className="max-h-[320px] overflow-y-auto px-5 sm:px-6">
                {isCartLoading ? (
                  <div className="space-y-4 py-5">
                    {[0, 1].map((i) => (
                      <div key={i} className="flex animate-pulse gap-3">
                        <div className="h-16 w-16 rounded-lg bg-gray-100" />
                        <div className="flex-1 space-y-2 py-1">
                          <div className="h-3 w-2/3 rounded bg-gray-100" />
                          <div className="h-3 w-1/3 rounded bg-gray-100" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : cart.length === 0 ? (
                  <div className="py-10 text-center">
                    <p className="font-medium text-gray-900">
                      Your cart is empty
                    </p>
                    <button
                      onClick={() => navigate("/")}
                      className="mt-3 text-sm font-semibold underline underline-offset-4"
                    >
                      Continue shopping
                    </button>
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {cart.map((item) => (
                      <li key={item.id} className="flex gap-3 py-4">
                        <div className="relative shrink-0">
                          <img
                            src={
                              item.product?.default_image || "/placeholder.svg"
                            }
                            alt={item.product?.name}
                            className="h-16 w-16 rounded-lg border border-gray-200 object-cover"
                          />
                          <span className="absolute -right-2 -top-2 grid h-5 min-w-[20px] place-items-center rounded-full bg-gray-900 px-1 text-[11px] font-bold text-white">
                            {item.quantity}
                          </span>
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-gray-900">
                            {item.product?.name}
                          </p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            ₹{money(item.price)} each
                          </p>
                        </div>

                        <p className="shrink-0 text-sm font-semibold tabular-nums">
                          ₹{money(item.total)}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Totals */}
              <div className="border-t border-gray-200 px-5 py-4 text-sm sm:px-6">
                <div className="flex justify-between py-1 text-gray-600">
                  <span>Subtotal</span>
                  <span className="tabular-nums">₹{money(total)}</span>
                </div>
                <div className="flex justify-between py-1 text-gray-600">
                  <span>Shipping</span>
                  <span className="tabular-nums">
                    {shipping === 0 ? "Free" : `₹${money(shipping)} on COD`}
                  </span>
                </div>
                {Number(promoDiscount || 0) > 0 && (
                  <div className="flex justify-between py-1 text-emerald-600">
                    <span>
                      Discount{promoCode ? ` · ${promoCode}` : ""}
                    </span>
                    <span className="tabular-nums">
                      − ₹{money(discount)}
                    </span>
                  </div>
                )}

                <div className="mt-3 flex items-baseline justify-between border-t border-gray-200 pt-3">
                  <span className="font-semibold">Total if paid online</span>
                  <span className="text-xl font-bold tabular-nums">
                    ₹{money(prepaidTotal)}
                  </span>
                </div>
                {shipping > 0 && (
                  <div className="mt-1 flex items-baseline justify-between text-gray-500">
                    <span>Total with cash on delivery</span>
                    <span className="font-semibold tabular-nums">
                      ₹{money(codTotal)}
                    </span>
                  </div>
                )}
                <p className="mt-2 text-right text-xs text-gray-500">
                  Inclusive of all taxes
                </p>
              </div>

              {/* Actions */}
              <div className="space-y-3 border-t border-gray-200 bg-gray-50 px-5 py-5 sm:px-6">
                <button
                  onClick={handlePayU}
                  disabled={!canPay}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full py-4 text-sm font-bold uppercase tracking-wider text-white transition hover:brightness-90 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:hover:brightness-100"
                  style={{ background: canPay ? ACCENT : undefined }}
                >
                  <Wallet size={17} />
                  {placingOrder
                    ? "Processing…"
                    : `Pay online · ₹${money(prepaidTotal)}`}
                </button>

                <button
                  onClick={handleCODClick}
                  disabled={!canPay}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-gray-900 py-4 text-sm font-bold uppercase tracking-wider transition hover:bg-gray-900 hover:text-white disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent"
                >
                  <Truck size={17} />
                  Cash on delivery · ₹{money(codTotal)}
                </button>

                {shipping > 0 && (
                  <p className="text-center text-xs text-gray-500">
                    Cash on delivery adds ₹{money(shipping)} shipping.
                  </p>
                )}

                <p className="text-center text-xs text-gray-500">
                  You'll be redirected to PayU's secure gateway.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* ---------------- COD confirm ---------------- */}
      {showCODModal && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-end justify-center bg-gray-950/60 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={handleCODCancel}
        >
          <div
            className="w-full max-w-md rounded-t-3xl bg-white p-6 sm:rounded-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold tracking-tight">
                  Confirm your order
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Pay in cash when it arrives.
                </p>
              </div>
              <button
                onClick={handleCODCancel}
                aria-label="Close"
                className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-900"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-6 rounded-2xl bg-gray-50 p-5 text-sm">
              <div className="flex justify-between py-1 text-gray-600">
                <span>Products</span>
                <span className="tabular-nums">₹{money(total)}</span>
              </div>
              <div className="flex justify-between py-1 text-gray-600">
                <span>Shipping</span>
                <span className="tabular-nums">₹{money(shipping)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between py-1 text-emerald-600">
                  <span>Discount</span>
                  <span className="tabular-nums">
                    − ₹{money(discount)}
                  </span>
                </div>
              )}
              <div className="mt-3 flex items-baseline justify-between border-t border-gray-200 pt-3">
                <span className="font-semibold">Amount due</span>
                <span className="text-xl font-bold tabular-nums">
                  ₹{money(codTotal)}
                </span>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-2 sm:flex-row-reverse">
              <button
                onClick={handleCODConfirm}
                disabled={placingOrder}
                className="h-12 w-full rounded-full text-sm font-bold uppercase tracking-wider text-white transition hover:brightness-90 disabled:bg-gray-300"
                style={{ background: placingOrder ? undefined : ACCENT }}
              >
                {placingOrder ? "Placing…" : "Place order"}
              </button>
              <button
                onClick={handleCODCancel}
                className="h-12 w-full rounded-full border border-gray-300 text-sm font-semibold transition hover:border-gray-900"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentPage;